import { Button, Grid, MenuItem, Select, Stack, TextField, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import dayjs from 'dayjs'
import { FormikProps } from 'formik'
import { FC } from 'react'
import * as yup from 'yup'
import {
    ReplyContentAudioDTO,
    ReplyContentDocumentDTO,
    ReplyContentImageDTO,
    ReplyContentVideoDTO,
    ReplyScheduleRepeatEnum,
    ReplyScheduleWeekDaysEnum,
} from '../../api/gen/base'
import CalendarIcon from '../../assets/icons/Calendar.svg?react'
import RefreshClockIcon from '../../assets/icons/RefreshClock.svg?react'
import {
    DAYS_OF_WEEK,
    regularRequestRepeatOptions,
} from '../../constants/regularRequests.constants'
import { getRegularRequestFormStartDate } from '../../utils/regularRequests'
import { FileUploader } from './components/FileUploader'

export type RegularRequestFormContent =
    | ReplyContentImageDTO
    | ReplyContentVideoDTO
    | ReplyContentAudioDTO
    | ReplyContentDocumentDTO

export type RegularRequestFormFile = RegularRequestFormContent | File

type RequestSchedule = {
    startDateTime: dayjs.Dayjs
    repeatType: ReplyScheduleRepeatEnum
    repeatDays?: ReplyScheduleWeekDaysEnum[]
}

export type RegularRequestFormValues = {
    files: RegularRequestFormFile[]
    text: string
    requestSchedule: RequestSchedule[]
}

export const regularRequestFormValidationSchema = yup.object({
    files: yup.array().optional(),
    requestSchedule: yup
        .array()
        .of(
            yup.object().shape({
                startDateTime: yup
                    .object()
                    .test(
                        'future_start_date_test',
                        'Start date must be in future.',
                        function (value) {
                            const date = value as dayjs.Dayjs | null

                            return (
                                !!date &&
                                date.isValid() &&
                                date.isAfter(new Date()) &&
                                date.get('year') <= 2099
                            )
                        },
                    )
                    .required(),
                repeatType: yup.string().trim().required(),
                repeatDays: yup
                    .array()
                    .of(yup.string())
                    .test(
                        'is_repeat_days_selected',
                        '"repeatDays" days is required',
                        function (value, context) {
                            const requestSchedule = context.from?.[0].value as
                                | RequestSchedule
                                | undefined

                            return requestSchedule?.repeatType === 'WEEK_DAYS'
                                ? Array.isArray(value) && value.length > 0
                                : true
                        },
                    ),
            }),
        )
        .min(1)
        .required(),
    text: yup.string().trim().required(),
})

type Props = {
    formik: FormikProps<RegularRequestFormValues>
    disabled?: boolean
}

export const RegularRequestForm: FC<Props> = ({ formik, disabled }) => {
    return (
        <form onSubmit={formik.handleSubmit}>
            <Stack gap={3}>
                <Stack
                    sx={{
                        padding: '16px',
                        gap: '16px',
                        borderRadius: '16px',
                        border: '1px solid #E7E8EE',

                        ':hover': {
                            borderRadius: '16px',
                        },
                    }}>
                    <Typography variant="h6" color="#5A5F72">
                        Расписание для отправки запроса
                    </Typography>
                    {formik.values.requestSchedule.map((schedule, index) => (
                        <Stack gap={2} key={index}>
                            <Stack direction="row" alignItems="center" gap={2}>
                                <CalendarIcon color="#8F93A3" width={24} height={24} />
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <DatePicker
                                        name={`requestSchedule.${index}.startDateTime`}
                                        onChange={(newValue) => {
                                            formik.setFieldValue(
                                                `requestSchedule.${index}.startDateTime`,
                                                newValue,
                                            )
                                        }}
                                        onError={(err) => {
                                            formik.setFieldError(
                                                `requestSchedule.${index}.startDateTime`,
                                                err ?? undefined,
                                            )
                                        }}
                                        slotProps={{
                                            popper: {
                                                sx: {
                                                    ['.MuiPaper-root.MuiPickersPopper-paper']: {
                                                        boxShadow: '0px 4px 33px 0px #0000000F',
                                                    },
                                                },
                                            },
                                        }}
                                        sx={{
                                            '.MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputAdornedEnd':
                                                {
                                                    paddingRight: 0,
                                                },
                                        }}
                                        value={schedule.startDateTime}
                                        disablePast
                                        format="DD.MM.YYYY"
                                        disabled={disabled}
                                    />
                                    <TimePicker
                                        name={`requestSchedule.${index}.startDateTime`}
                                        onChange={(newValue) => {
                                            const roundedValue = newValue
                                                ? dayjs(
                                                      getRegularRequestFormStartDate(
                                                          newValue.toDate(),
                                                      ),
                                                  )
                                                : null

                                            formik.setFieldValue(
                                                `requestSchedule.${index}.startDateTime`,
                                                roundedValue,
                                            )
                                        }}
                                        slotProps={{
                                            popper: {
                                                sx: {
                                                    ['.MuiPaper-root.MuiPickersPopper-paper']: {
                                                        boxShadow: '0px 4px 33px 0px #0000000F',
                                                    },
                                                },
                                            },
                                        }}
                                        timeSteps={{ minutes: 15 }}
                                        value={schedule.startDateTime}
                                        disablePast
                                        disableIgnoringDatePartForTimeValidation
                                        format="HH:mm"
                                        ampmInClock={false}
                                        minutesStep={15}
                                        disabled={disabled}
                                        sx={{
                                            width: '130px',
                                            minWidth: '105px',
                                            '.MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputAdornedEnd':
                                                {
                                                    paddingRight: 0,
                                                },
                                        }}
                                    />
                                </Stack>
                            </Stack>
                            <Stack direction="row" alignItems="center" gap={2}>
                                <RefreshClockIcon color="#8F93A3" width={24} height={24} />
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <Select
                                        MenuProps={{
                                            anchorOrigin: {
                                                vertical: 'top',
                                                horizontal: 'center',
                                            },
                                            transformOrigin: {
                                                vertical: 'top',
                                                horizontal: 'left',
                                            },
                                        }}
                                        name={`requestSchedule.${index}.repeatType`}
                                        disabled={disabled}
                                        onChange={(e) => {
                                            formik.setFieldValue(
                                                `requestSchedule.${index}.repeatDays`,
                                                undefined,
                                            )
                                            formik.handleChange(e)
                                        }}
                                        value={schedule.repeatType}
                                        sx={{ mr: 1, overflow: 'auto' }}
                                        color="secondary">
                                        {regularRequestRepeatOptions.map(({ value, label }) => {
                                            return (
                                                <MenuItem
                                                    value={value}
                                                    key={value}
                                                    sx={{
                                                        borderBottom: '1px solid #F4F4F6',
                                                        p: '10px 16px',
                                                    }}>
                                                    <Grid
                                                        container
                                                        alignItems="center"
                                                        sx={{ width: '100%', m: 0 }}>
                                                        <Typography variant="h6">
                                                            {label}
                                                        </Typography>
                                                    </Grid>
                                                </MenuItem>
                                            )
                                        })}
                                    </Select>
                                </Stack>
                            </Stack>
                            {schedule.repeatType === 'WEEK_DAYS' && (
                                <Stack direction="column" gap={2}>
                                    <Typography variant="h6" color="#5A5F72">
                                        Выберите дни повтора
                                    </Typography>
                                    <Stack direction="row" alignItems="center" gap={1}>
                                        {DAYS_OF_WEEK.map(({ label, value }) => {
                                            const selected = schedule.repeatDays?.includes(value)

                                            const handleToggle = () => {
                                                if (selected) {
                                                    formik.setFieldValue(
                                                        `requestSchedule.${index}.repeatDays`,
                                                        schedule.repeatDays?.filter(
                                                            (day) => day !== value,
                                                        ),
                                                    )
                                                } else {
                                                    formik.setFieldValue(
                                                        `requestSchedule.${index}.repeatDays`,
                                                        (schedule.repeatDays ?? []).concat(value),
                                                    )
                                                }
                                            }

                                            return (
                                                <Button
                                                    key={value}
                                                    sx={{
                                                        width: '40px',
                                                        minWidth: '40px',
                                                        height: '40px',
                                                        p: 1,
                                                        backgroundColor: selected
                                                            ? '#004B9F'
                                                            : '#F9FAFB',
                                                        color: selected ? '#FFFFFF' : '#181B25',

                                                        ':hover': {
                                                            backgroundColor: selected
                                                                ? '#004B9F'
                                                                : '#F9FAFB',
                                                        },
                                                    }}
                                                    onClick={handleToggle}>
                                                    {label}
                                                </Button>
                                            )
                                        })}
                                    </Stack>
                                </Stack>
                            )}
                        </Stack>
                    ))}
                </Stack>
                <Stack gap={1}>
                    <Typography variant="h5">Текст запроса</Typography>
                    <TextField
                        multiline
                        rows={4}
                        sx={{
                            '& .MuiOutlinedInput-root.MuiInputBase-colorPrimary': {
                                borderRadius: '16px',
                                backgroundColor: '#F9FAFB',
                                border: '1px solid #F4F4F6',
                            },
                        }}
                        name="text"
                        placeholder="Введите текст запроса"
                        disabled={disabled}
                        value={formik.values.text}
                        fullWidth
                        onChange={formik.handleChange}
                    />
                </Stack>
                <Stack gap={1}>
                    <Typography variant="h5">Вложения запроса</Typography>
                    <FileUploader
                        files={formik.values.files}
                        disabled={disabled}
                        onChange={(list) => {
                            formik.setFieldValue('files', list)
                        }}
                    />
                </Stack>
            </Stack>
        </form>
    )
}
